import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Hand, LayoutGrid, Hash, Heart, Briefcase, Coins, Users, Star, Calendar, ArrowRight, Zap, ShieldCheck, FileText, Mail, PlayCircle, Compass, Brain, Clock, Lock } from "lucide-react";
import { REPORTS } from "../lib/reports-data";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import ZodiacWheel from "../components/ZodiacWheel";
import Seo, { ORGANIZATION_LD, WEBSITE_LD, faqLd, softwareApplicationLd, speakableLd } from "../components/Seo";

const ICONS = { sparkles: Sparkles, hand: Hand, layout: LayoutGrid, hash: Hash, heart: Heart, briefcase: Briefcase, coins: Coins, users: Users, star: Star, calendar: Calendar };

const ZODIAC_MARQUEE = ["Aries ♈", "Taurus ♉", "Gemini ♊", "Cancer ♋", "Leo ♌", "Virgo ♍", "Libra ♎", "Scorpio ♏", "Sagittarius ♐", "Capricorn ♑", "Aquarius ♒", "Pisces ♓"];

export default function Landing() {
  const landingFaqs = [
    { q: "What is Bhavishyavani?", a: "Bhavishyavani is an AI-powered Vedic astrology platform: a deterministic Kundli engine (Swiss Ephemeris, Lahiri ayanamsa) plus 10 premium AI reports at ₹699 each — Kundli, Palmistry, Tarot, Numerology, Love, Career and more, delivered as a beautiful PDF." },
    { q: "How is Bhavishyavani different from other astrology sites?", a: "Every calculation is deterministic — planetary positions, houses, nakshatra and Vimshottari dasha come from Swiss Ephemeris, not an LLM. AI only interprets the calculated facts, so scores and predictions are reproducible and versioned (engine v1.0.0)." },
    { q: "How much does a report cost?", a: "Every premium AI report costs a flat ₹699 (INR). No subscription, no hidden fees — one-time payment for lifetime access to your personalized report." },
    { q: "How quickly do I receive my report?", a: "Reports are generated by AI in under 60 seconds. The PDF is emailed to your inbox and available on your dashboard immediately." },
    { q: "Is my birth data safe?", a: "Yes. We use HTTPS, encrypted sessions, and store your data only to generate your report. Palm photos are auto-deleted after 90 days. Read our Privacy Policy for details." },
    { q: "Which payment methods do you accept?", a: "We accept UPI, credit cards, debit cards, and netbanking via Razorpay — India's most trusted payment gateway." },
  ];

  const softwareLd = softwareApplicationLd({
    name: "Bhavishyavani — AI Vedic Astrology",
    description: "Cast your Vedic Kundli, get living wealth & career scores, opportunity calendar and grounded Ask-AI — all deterministic, all AI-interpreted.",
    path: "/",
  });

  return (
    <div data-testid="landing-page" className="relative">
      <Seo
        title="Bhavishyavani — AI Vedic Astrology · Kundli, Career, Love & Wealth Reports"
        description="Bhavishyavani is an AI-powered Vedic astrology platform: 10 in-depth personalised AI reports — Kundli, Career, Love, Wealth, Numerology, Palmistry, Tarot and more — at ₹699 each, delivered as PDF. Trusted Swiss Ephemeris engine · Lahiri ayanamsa. Also known as Bhavishyani, Bhavisyavani, Bhavishwani."
        path="/"
        keywords="bhavishyavani, bhavishyavani app, ai bhavishyavani, bhavishyavani chart, bhavishyavani kundli, ai vedic astrology, kundli online, birth chart calculator, ₹699 astrology report"
        jsonLd={[ORGANIZATION_LD, WEBSITE_LD, softwareLd, faqLd(landingFaqs), speakableLd(["[data-testid='hero-headline']", "[data-testid='hero-eyebrow']"])]}
      />
      <Nav />

      {/* HERO — Ancient Jyotish. Modern Intelligence. */}
      <section className="relative overflow-hidden night-panel" data-testid="hero-section">
        <div className="absolute inset-0 starfield" />
        {/* Soft golden aura behind the wheel — desktop only */}
        <div className="hidden md:block absolute right-[-8%] top-[10%] w-[560px] h-[560px] rounded-full pointer-events-none"
             style={{ background: "radial-gradient(closest-side, rgba(198,161,91,0.22), transparent 70%)", filter: "blur(20px)" }} />

        {/* Distant nebula wisps — desktop only, kept from bleeding on mobile */}
        <div className="hidden md:block absolute left-[-10%] top-[30%] w-[500px] h-[220px] pointer-events-none opacity-40"
             style={{ background: "radial-gradient(ellipse at center, rgba(96,102,180,0.35), transparent 70%)", filter: "blur(40px)" }} />
        <div className="hidden md:block absolute right-[8%] bottom-[10%] w-[380px] h-[180px] pointer-events-none opacity-40"
             style={{ background: "radial-gradient(ellipse at center, rgba(163,124,72,0.35), transparent 70%)", filter: "blur(40px)" }} />

        {/* Sunset horizon glow */}
        <div className="absolute inset-x-0 bottom-0 h-[220px] pointer-events-none"
             style={{ background: "linear-gradient(180deg, transparent 0%, rgba(198,161,91,0.10) 55%, rgba(198,161,91,0.22) 80%, rgba(11,16,32,0.9) 100%)" }} />

        {/* Mountain silhouette */}
        <svg className="absolute inset-x-0 bottom-0 w-full pointer-events-none opacity-90"
             height="140" viewBox="0 0 1440 140" preserveAspectRatio="none" aria-hidden="true">
          <defs>
            <linearGradient id="mountain-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#0F1730" stopOpacity="0.95" />
              <stop offset="100%" stopColor="#050813" stopOpacity="1" />
            </linearGradient>
          </defs>
          <path d="M0,140 L0,90 L120,60 L220,80 L320,40 L450,75 L560,55 L680,85 L820,50 L950,80 L1080,60 L1200,95 L1330,70 L1440,90 L1440,140 Z"
                fill="url(#mountain-gradient)" />
          {/* Lake reflection — thin gold streak */}
          <path d="M340,140 L1100,140 L1080,138 L360,138 Z"
                fill="#C6A15B" opacity="0.15" />
        </svg>
        <div className="relative max-w-7xl mx-auto px-6 sm:px-10 pt-16 sm:pt-20 pb-20 sm:pb-24">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* LCP-critical: no opacity/y initial state — the hero H1 and
                paragraph are the LCP element on mobile. Any opacity fade
                here adds ~700ms element-render-delay to Core Web Vitals. */}
            <div className="min-w-0">
              <div className="inline-flex items-center gap-2 mb-6 text-xs uppercase tracking-[0.32em] text-[#DCCB9A]" data-testid="hero-eyebrow">
                <Sparkles className="w-3.5 h-3.5 text-[#C6A15B]" /> Your Cosmic Blueprint
              </div>

              <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl leading-[1.05] text-white break-words" data-testid="hero-headline">
                Ancient Jyotish. <br />
                <span className="gold-text italic">Modern Intelligence.</span>
              </h1>

              <p className="mt-6 sm:mt-7 text-base sm:text-lg text-[#B7B9C9] max-w-lg leading-relaxed">
                AI-powered Vedic astrology that evolves with the planets. Personalized insights for a better tomorrow.
              </p>

              <div className="mt-8 sm:mt-9 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 sm:gap-4">
                <Link data-testid="hero-cta-blueprint" to="/reports"
                  className="btn-gold rounded-full px-6 py-3.5 text-base inline-flex items-center justify-center gap-2.5 shadow-[0_18px_40px_-16px_rgba(198,161,91,0.55)]">
                  <Sparkles className="w-4 h-4" /> Generate My Cosmic Blueprint
                </Link>
                <Link data-testid="hero-cta-how" to="/how-it-works"
                  className="rounded-full px-6 py-3.5 border border-white/20 text-white hover:border-[#C6A15B]/60 hover:text-[#DCCB9A] transition inline-flex items-center justify-center gap-2.5">
                  <PlayCircle className="w-4 h-4" /> See How It Works
                </Link>
              </div>

              {/* Trust bar — real engine credentials + live counts (no fake numbers) */}
              <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3" data-testid="hero-trust-bar">
                <div className="inline-flex items-center gap-2 text-xs text-[#B7B9C9]">
                  <ShieldCheck className="w-4 h-4 text-[#C6A15B]" />
                  Swiss Ephemeris · Lahiri Ayanamsa
                </div>
                <RealStats />
              </div>
            </div>

            <div
              className="relative flex items-center justify-center min-w-0 w-full"
              data-testid="hero-wheel-wrap"
            >
              <div className="relative animate-float w-full max-w-[560px] aspect-square">
                <ZodiacWheel size={560} />
              </div>
            </div>
          </div>
        </div>

        {/* Feature strip pinned to bottom of hero */}
        <div className="relative border-t border-white/10 bg-[rgba(11,16,32,0.6)] backdrop-blur-sm" data-testid="hero-feature-strip">
          <div className="max-w-7xl mx-auto px-6 sm:px-10 py-6 grid grid-cols-2 md:grid-cols-4 gap-6">
            <Feature icon={Compass} title="Accurate Charts" desc="Swiss Ephemeris powered Vedic calculations" />
            <Feature icon={Brain}   title="AI Insights"    desc="Personalized predictions that evolve with time" />
            <Feature icon={Clock}   title="Timing That Matters" desc="Know the right time for every important decision" />
            <Feature icon={Lock}    title="Privacy First"  desc="Your data is secure and never shared" />
          </div>
        </div>
      </section>

      {/* REPORT GRID */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 py-24" data-testid="reports-overview" style={{ contentVisibility: "auto", containIntrinsicSize: "1200px" }}>
        <div className="flex items-end justify-between mb-12 flex-wrap gap-6">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#B8860B] mb-3 font-semibold">Ten AI Reports</p>
            <h2 className="font-display text-4xl sm:text-5xl text-[#0B1020] max-w-xl leading-tight">
              Every report grounded in your <span className="gold-text italic">real Kundli.</span>
            </h2>
          </div>
          <Link to="/reports" className="text-sm text-[#1E1B7A] hover:underline underline-offset-4 font-medium" data-testid="see-all-reports">
            See all 10 →
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {REPORTS.slice(0, 6).map((r) => {
            const Icon = ICONS[r.icon];
            return (
              <Link key={r.id} to={`/reports/${r.id}`} data-testid={`report-card-${r.id}`}
                className="card-premium p-8 flex flex-col group">
                <div className="flex items-start justify-between">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#FDE68A]/40 to-[#D4AF37]/20 border border-[#D4AF37]/50 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-[#B8860B]" />
                  </div>
                  <span className="text-xs px-3 py-1 rounded-full border border-[#D4AF37]/40 text-[#8B6914] bg-[#FFFBE6] font-semibold">₹699</span>
                </div>
                <h3 className="font-display text-2xl text-[#1E1B4B] mt-6 leading-tight">{r.title}</h3>
                <p className="text-sm text-[#64748B] mt-3 leading-relaxed">{r.tagline}</p>
                <div className="mt-6 pt-6 border-t border-[#1E1B7A]/8 text-xs text-[#64748B] flex items-center justify-between">
                  <span>{r.includes.length} in-depth sections</span>
                  <span className="text-[#1E1B7A] font-medium group-hover:translate-x-1 transition-transform">Get yours →</span>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* VALUE PROP */}
      <section className="relative max-w-7xl mx-auto px-6 sm:px-10 py-24" data-testid="value-prop" style={{ contentVisibility: "auto", containIntrinsicSize: "600px" }}>
        <div className="grid md:grid-cols-3 gap-6">
          <ValueCard icon={Zap} title="Deterministic Vedic" desc="Swiss Ephemeris + Lahiri ayanamsa. Same inputs, same chart, forever." />
          <ValueCard icon={FileText} title="Premium PDF" desc="Cormorant typography, beautifully formatted, ready to save or share." />
          <ValueCard icon={Mail} title="Email Delivery" desc="Inbox copy plus dashboard access. Nothing lost." />
          <ValueCard icon={ShieldCheck} title="Secure Payments" desc="Razorpay-powered checkout. UPI, cards, netbanking." />
          <ValueCard icon={Sparkles} title="AI × Jyotish" desc="AI interprets your calculated chart. It never calculates it." />
          <ValueCard icon={Star} title="One-time ₹699" desc="No subscription. No hidden fees. Lifetime access." />
        </div>
      </section>

      {/* PREMIUM REPORTS CTA */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 py-24" data-testid="premium-cta" style={{ contentVisibility: "auto", containIntrinsicSize: "500px" }}>
        <div className="night-panel rounded-[28px] p-10 sm:p-14 relative overflow-hidden">
          <div className="absolute inset-0 starfield opacity-40" />
          <div className="absolute -top-16 -right-16 w-72 h-72 rounded-full bg-[#C6A15B]/20 blur-3xl" />
          <div className="relative flex flex-col md:flex-row items-start md:items-center gap-8 justify-between">
            <div className="max-w-xl">
              <p className="text-xs uppercase tracking-[0.3em] text-[#DCCB9A] mb-3 font-semibold">₹699 flat · lifetime access</p>
              <h2 className="font-display text-4xl text-white leading-tight">
                Your chart, deeply read. <span className="gold-text italic">Ten ways.</span>
              </h2>
              <p className="mt-4 text-[#B7B9C9]">Career, Wealth, Love, Yearly outlook, Palmistry, Tarot — every report grounded in the same deterministic Kundli.</p>
            </div>
            <Link to="/reports" className="btn-gold rounded-full px-7 py-3.5 text-base inline-flex items-center gap-2" data-testid="cta-premium-reports">
              See all reports <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function Stat({ value, label }) {
  return (
    <div>
      <div className="font-display text-3xl gold-text">{value}</div>
      <div className="text-xs text-[#94A3B8] uppercase tracking-widest mt-1">{label}</div>
    </div>
  );
}

function Feature({ icon: Icon, title, desc }) {
  return (
    <div className="flex items-start gap-3" data-testid={`hero-feature-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="w-10 h-10 rounded-full border border-[#C6A15B]/40 bg-white/[0.03] flex items-center justify-center flex-shrink-0">
        <Icon className="w-4.5 h-4.5 text-[#C6A15B]" style={{ width: 18, height: 18 }} />
      </div>
      <div>
        <div className="text-sm font-semibold text-white">{title}</div>
        <div className="text-xs text-[#B7B9C9] leading-relaxed mt-0.5">{desc}</div>
      </div>
    </div>
  );
}

function RealStats() {
  const [s, setS] = useState(null);
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v2/stats`)
      .then(r => r.ok ? r.json() : null)
      .then(setS)
      .catch(() => setS(null));
  }, []);
  if (!s) return null;
  // Only show if we have honest, non-trivial counts.
  const bits = [];
  if (s.kundlis_cast >= 10) bits.push(`${s.kundlis_cast.toLocaleString()}+ kundlis cast`);
  if (s.reports_generated >= 10) bits.push(`${s.reports_generated.toLocaleString()}+ reports generated`);
  if (bits.length === 0) return null;
  return (
    <div className="inline-flex items-center gap-2 text-xs text-[#B7B9C9]" data-testid="hero-real-stats">
      <span className="w-1.5 h-1.5 rounded-full bg-[#3F7D62]" />
      {bits.join(" · ")}
    </div>
  );
}

function ValueCard({ icon: Icon, title, desc }) {
  return (
    <div className="glass rounded-2xl p-7">
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FDE68A]/30 to-[#D4AF37]/10 border border-[#D4AF37]/40 flex items-center justify-center">
        <Icon className="w-5 h-5 text-[#B8860B]" />
      </div>
      <h3 className="font-display text-2xl text-[#1E1B4B] mt-4">{title}</h3>
      <p className="text-sm text-[#475569] mt-2 leading-relaxed">{desc}</p>
    </div>
  );
}

function NavTile({ to, label, icon }) {
  return (
    <Link to={to} data-testid={`nav-tile-${label.toLowerCase().replace(/ /g, "-")}`}
      className="group relative overflow-hidden rounded-2xl aspect-square p-3 flex flex-col items-center justify-center transition-all duration-200 active:scale-95 hover:-translate-y-0.5"
      style={{
        background: "linear-gradient(160deg, rgba(252,250,245,0.06) 0%, rgba(198,161,91,0.10) 100%)",
        border: "1px solid rgba(198,161,91,0.28)",
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04), 0 8px 22px -12px rgba(0,0,0,0.4)",
      }}>
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity"
           style={{ background: "linear-gradient(160deg, rgba(220,203,154,0.08) 0%, rgba(198,161,91,0.14) 100%)" }} />
      <div className="text-3xl sm:text-4xl leading-none relative" aria-hidden style={{ filter: "grayscale(0.15) brightness(1.05)" }}>{icon}</div>
      <div className="mt-2 text-[10px] sm:text-xs font-medium text-center leading-tight relative"
           style={{ color: "#EDE7D4", fontFamily: "var(--font-body)", letterSpacing: "0.02em" }}>
        {label}
      </div>
    </Link>
  );
}

